import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

export interface MergeGroup {
  id: string;
  name: string;
  tableIds: string[];
  totalCapacity: number;
  restaurantTableId?: Array<{ id: number; name: string }>;
  color?: string;
}

interface MergeGroupContextType {
  mergeGroups: MergeGroup[];
  setMergeGroups: (groups: MergeGroup[]) => void;
  refreshMergeGroups: (date?: string, floorId?: number) => Promise<void>;
  removeMergeGroup: (groupId: string) => void;
  getMergeGroupByTableId: (tableId: string) => MergeGroup | undefined;
}

const MergeGroupContext = createContext<MergeGroupContextType | undefined>(undefined);



export const MergeGroupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mergeGroups, setMergeGroupsState] = useState<MergeGroup[]>([]);
  
  // Load merge groups from backend API instead of localStorage
  // Wrapped in useCallback to provide stable reference for useEffect dependencies
  const loadMergeGroupsFromBackend = useCallback(async (date?: string, floorId?: number) => {
    try {
      const { tableMergeApi } = await import('../services/tableMergeApi');
      const backendGroups = await tableMergeApi.getAllMergeGroups(date, floorId);
      //console.log('✅ Loaded merge groups from backend:', backendGroups);
      
      // Convert backend merge groups to context format (tableIds as strings)
      const contextGroups: MergeGroup[] = backendGroups.map(group => ({
        id: group.id,
        name: group.name,
        tableIds: group.tableIds.map(id => String(id)), // Convert numbers to strings
        totalCapacity: group.totalCapacity,
        restaurantTableId: group.tables?.map(t => ({ id: t.id, name: t.name })) || [],
        color: group.color
      }));
      
      setMergeGroupsState(contextGroups);
    } catch (error) {

      // Don't show error toast - fail silently for better UX
    }
  }, []); // Empty deps - uses dynamic import and setState which are stable

  // Note: Initial load removed because backend now requires floorId
  // The AdvancedFloorPlanEditor's useEffect handles loading merge groups when a floor is selected

  // Stable reference for context consumers - prevents infinite loops in useEffect dependencies
  const setMergeGroups = useCallback((groups: MergeGroup[]) => {
    setMergeGroupsState(groups);
  }, []);

  // Stable reference - CRITICAL: This fixes the infinite refresh loop in AdvancedFloorPlanEditor
  const refreshMergeGroups = useCallback(async (date?: string, floorId?: number) => {
    await loadMergeGroupsFromBackend(date, floorId);
  }, [loadMergeGroupsFromBackend]);

  const removeMergeGroup = useCallback((groupId: string) => {
    setMergeGroupsState(prev => prev.filter(g => g.id !== groupId));
  }, []);

  // Note: getMergeGroupByTableId depends on mergeGroups state, so it will change when mergeGroups changes
  // This is intentional - consumers need the updated function to find groups in current state
  const getMergeGroupByTableId = useCallback((tableId: string): MergeGroup | undefined => {
    return mergeGroups.find(group => group.tableIds.includes(tableId));
  }, [mergeGroups]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => ({
    mergeGroups,
    setMergeGroups,
    refreshMergeGroups,
    removeMergeGroup,
    getMergeGroupByTableId
  }), [mergeGroups, setMergeGroups, refreshMergeGroups, removeMergeGroup, getMergeGroupByTableId]);

  return (
    <MergeGroupContext.Provider value={contextValue}>
      {children}
    </MergeGroupContext.Provider>
  );
};

export const useMergeGroups = (): MergeGroupContextType => {
  const context = useContext(MergeGroupContext);
  if (!context) {
    throw new Error('useMergeGroups must be used within a MergeGroupProvider');
  }
  return context;
};
